import { Box, Typography } from '@mui/material';
import dayjs from '../../utils/dayjs';
import { CalendarEvent } from './types';
import CalendarEventPill from './CalendarEventPill';
import { calendarUiPalette } from './calendarPalette';

const MAX_VISIBLE_EVENTS = 3;

interface CalendarDayCellProps {
  date: Date;
  isCurrentMonth: boolean;
  isSelected: boolean;
  isToday: boolean;
  events: CalendarEvent[];
  onSelectDate: (date: Date) => void;
  onSelectMore: (date: Date) => void;
  onSelectEvent: (event: CalendarEvent) => void;
  onDoubleClickEvent: (event: CalendarEvent) => void;
}

export default function CalendarDayCell({
  date,
  isCurrentMonth,
  isSelected,
  isToday,
  events,
  onSelectDate,
  onSelectMore,
  onSelectEvent,
  onDoubleClickEvent,
}: Readonly<CalendarDayCellProps>) {
  const visibleEvents = events.slice(0, MAX_VISIBLE_EVENTS);
  const hiddenCount = Math.max(events.length - visibleEvents.length, 0);

  return (
    <Box
      component="div"
      role="button"
      tabIndex={0}
      onClick={() => onSelectDate(date)}
      onKeyDown={(keyEvent) => {
        if (keyEvent.key === 'Enter' || keyEvent.key === ' ') {
          keyEvent.preventDefault();
          onSelectDate(date);
        }
      }}
      data-cy={`calendar-day-cell-${dayjs(date).format('YYYY-MM-DD')}`}
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        height: 160,
        p: 1.5,
        borderRadius: '12px',
        border: isSelected
          ? `1px solid ${calendarUiPalette.dayCell.selectedBorder}`
          : `1px solid ${calendarUiPalette.dayCell.defaultBorder}`,
        backgroundColor: isCurrentMonth
          ? calendarUiPalette.dayCell.currentMonthBackground
          : calendarUiPalette.dayCell.outsideMonthBackground,
        boxShadow: isSelected ? calendarUiPalette.shadows.softInset : calendarUiPalette.shadows.soft,
        textAlign: 'left',
        cursor: 'pointer',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography
          component="span"
          sx={{
            color: isCurrentMonth
              ? calendarUiPalette.dayCell.currentMonthDayText
              : calendarUiPalette.dayCell.outsideMonthDayText,
            fontFamily: '"Open Sans", Arial, sans-serif',
            fontSize: '0.7rem',
            fontWeight: 600,
            lineHeight: 1,
            letterSpacing: '0.12em',
          }}
        >
          {dayjs(date).date()}
        </Typography>
        {isToday ? (
          <Box
            component="span"
            sx={{
              width: 6,
              height: 6,
              borderRadius: '999px',
              backgroundColor: calendarUiPalette.dayCell.todayDot,
              opacity: 0.85,
              flexShrink: 0,
            }}
          />
        ) : null}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          minHeight: 0,
          flex: 1,
        }}
      >
        {visibleEvents.map((event) => (
          <CalendarEventPill
            key={`${event.logtype}-${event.id}`}
            event={event}
            muted={!isCurrentMonth}
            onClick={onSelectEvent}
            onDoubleClick={onDoubleClickEvent}
          />
        ))}
        {hiddenCount > 0 ? (
          <Typography
            component="span"
            onClick={(event) => {
              event.stopPropagation();
              onSelectMore(date);
            }}
            sx={{
              mt: 0.25,
              pl: 0.5,
              color: isCurrentMonth
                ? calendarUiPalette.dayCell.currentMonthMoreText
                : calendarUiPalette.dayCell.outsideMonthMoreText,
              fontSize: '10px',
              fontWeight: 700,
              cursor: 'pointer',
              textDecoration: 'underline',
              textDecorationColor: 'transparent',
              transition: 'text-decoration-color 120ms ease',
              '&:hover': {
                textDecorationColor: 'currentColor',
              },
            }}
          >
            +{hiddenCount} more
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}
